<template>
  <entity-list-layout
    class="admin-sensor-list"
    title="SENSOR"
    :count="sensors.length"
    :query.sync="search"
    :search-hint="$t('search_by_text')"
  >
    <template #title-area-content>
      <managed-by-select
        v-if="showsManagedBy"
        v-model="managedBy"
        class="admin-sensor-list__managed-by-select"
        :distributors="distributors"
      />
    </template>
    <p-data-list :pagination.sync="pagination" :data="sensors" :getItemProps="genItemProps">
      <data-field name="No" headerClass="column--no">
        <template v-slot="props">{{ props.index + 1 }}</template>
      </data-field>
      <data-field :name="$t('field.type')" value="type_index" headerClass="column--type" sortable>
        <template v-slot="props">
          <sensor-type-label :sensor="props.item" />
        </template>
      </data-field>
      <data-field :name="$t('field.identifier')" value="_identifier_sort_key" sortable headerClass="column--id">
        <template v-slot="props">
          <span
            v-if="showsIdentifier(props.item)"
            class="admin-sensor-list__sensor-id"
          >
            {{ props.item.device_id }}
          </span>
        </template>
      </data-field>
      <data-field :name="$t('field.name')" value="name" sortable headerClass="column--name">
        <template v-slot="props">
          <router-link
            :to="{ name: 'admin-sensor', params: { id: props.item.id }}"
            class="admin-sensor-list__sensor-name"
          >
            {{ props.item.name }}
          </router-link>
        </template>
      </data-field>
      <data-field
        :name="$t('field.installation_date')"
        value="installed_on"
        sortable
        headerClass="column--installed-on"
      >
        <template v-slot="props">{{ props.item.installedOnText }}</template>
      </data-field>
      <data-field :name="$t('field.threshold')" value="threshold" sortable headerClass="column--threshold">
        <template v-slot="props">
          <div :style="props.item | thresholdStyle">
            {{ props.item.thresholdText }}
          </div>
        </template>
      </data-field>
      <data-field :name="$t('field.place')" value="place" sortable headerClass="column--place">
        <template v-slot="props">
          <span class="admin-sensor-list__installation-site">{{ props.item.place }}</span>
        </template>
      </data-field>
      <data-field
        v-if="showsManagedBy"
        :name="$t('field.managed_by')"
        value="managedBySortKey"
        headerClass="column--managed-by"
        sortable
      >
        <template v-slot="props">
          <span
            v-if="props.item.managedByName"
            class="admin-sensor-list__managed-by"
            :class="{ 'text--disabled': props.item.managedByIsDirect }"
          >{{ props.item.managedByName }}</span>
          <span v-else data-testid="managed-by-empty">--</span>
        </template>
      </data-field>
      <data-field
        :name="$t('field.organization_name')"
        value="created_by.organization_name"
        headerClass="column--organization"
        sortable
      >
        <template v-slot="props">
          <router-link
            v-if="props.item.created_by.organization_id"
            :to="{ name: 'admin-organization', params: { id: props.item.created_by.organization_id }}"
          >
            {{ props.item.created_by.organization_name }}
          </router-link>
          <span v-else data-testid="organization-empty">--</span>
        </template>
      </data-field>
      <data-field :name="$t('field.created_by')" value="created_by.name" headerClass="column--name" sortable>
        <template v-slot="props">
          <router-link
            v-if="props.item.created_by.email"
            :to="{ name: 'admin-account', params: { email: props.item.created_by.email }}"
          >
            {{ props.item.created_by.name }}
          </router-link>
          <span v-else data-testid="created-by-empty">--</span>
        </template>
      </data-field>
    </p-data-list>
  </entity-list-layout>
</template>

<i18n lang="yaml">
ja:
  search_by_text: '文字列で検索'
  field:
    identifier: '識別子'
    name: 'センサー名'
    type: '種別'
    place: '設置場所'
    installation_date: '設置日'
    threshold: 'しきい値'
    organization_name: '組織名'
    created_by: '作成者'
    managed_by: '管理元'
  messages:
    failed_to_load: 'センサー一覧の読み込みに失敗しました'
    failed_to_load_distributors: '代理店の読み込みに失敗しました'

en:
  search_by_text: 'Search by text'
  field:
    identifier: 'Identifier'
    name: 'Sensor name'
    type: 'Type'
    place: 'Installation site'
    installation_date: 'Installation date'
    threshold: 'Threshold'
    organization_name: 'Organization name'
    created_by: 'Created by'
    managed_by: 'Managed by'
  messages:
    failed_to_load: 'Failed to load sensors'
    failed_to_load_distributors: 'Failed to load distributors'
</i18n>

<script>
import EntityListLayout from '@/components/atoms/EntityListLayout';
import SensorTypeLabel from '@/components/atoms/SensorTypeLabel';
import ManagedBySelect from '@/components/molecules/ManagedBySelect';
import {
  describeManagedBy, directLabel, extractDistributors, matchesManagedBy,
} from '@/libs/managedBy';
import DateTimeFormatting from '@/mixins/dateTimeFormatting';
import Features from '@/mixins/features';
import Notifications from '@/mixins/notifications';
import SensorTypes from '@/mixins/sensorTypes';
import DataList, { Field } from '../DataList';

const thresholdToString = (threshold) => {
  if (['null', 'undefined'].includes(threshold)) {
    return '-';
  }
  return String(threshold);
};

export default {
  name: 'admin-sensor-list',
  components: {
    DataField: Field,
    EntityListLayout,
    ManagedBySelect,
    PDataList: DataList,
    SensorTypeLabel,
  },
  mixins: [SensorTypes, DateTimeFormatting, Features, Notifications],
  created() {
    this.loadSensors();
  },
  computed: {
    // 管理元（選択欄・列・検索対象）は admin だけに見せる。代理店ログイン・代理ログイン中は admin が無く、
    // API も管理元を返さない。ログアウト直後は getCurrentUser が空になるため ?. で守る
    showsManagedBy() {
      return Boolean(this.$store.getters.getCurrentUser?.admin) && this.featureIsActive('distributor_account');
    },
    distributors() {
      return extractDistributors(this.$store.getters.getUsers.valuesArray());
    },
    sensors() {
      let sensors = this.$store.getters.getSensors;
      sensors = sensors.map((sensor) => ({
        ...sensor,
        threshold: String(sensor.threshold),
        thresholdText: thresholdToString(String(sensor.threshold)),
        installedOnText: this.formatDate(sensor.installed_on),
        type_index: this.SENSOR_TYPE_INDEXES[sensor.type],
        _identifier_sort_key: this.displayedIdentifier(sensor),
        ...this.managedByFields(sensor),
      }));
      sensors = this.filterSensors(sensors);
      sensors = this.sortSensors(sensors);
      return sensors;
    },
  },
  data() {
    return {
      pagination: {
        page: 1,
        itemsPerPage: 20,
        sortBy: 'type_index',
      },
      search: '',
      managedBy: null,
    };
  },
  filters: {
    thresholdStyle(sensor) {
      if (['null', 'undefined'].includes(sensor.threshold)) {
        return 'text-align: center;';
      }
      return 'text-align: right;';
    },
  },
  methods: {
    async loadSensors() {
      const requests = [this.$store.dispatch('requestSensors')];
      // 代理店名は users から引く。管理元を見せないときは取らない
      if (this.showsManagedBy) {
        requests.push(this.$store.dispatch('requestUsers'));
      }
      const [listResult, distributorsResult] = await Promise.allSettled(requests);
      // 画面遷移・代理ログインの切替・ログアウトで中断された取得（キャンセル）は通知しない
      const failed = result => result?.status === 'rejected' && !this.$http.isCancel(result.reason);
      // 通知は 1 件しか表示されないため、一覧そのものの失敗を優先する
      if (failed(listResult)) {
        this.notifyError('messages.failed_to_load');
      } else if (failed(distributorsResult)) {
        this.notifyError('messages.failed_to_load_distributors');
      }
    },
    managedByFields(item) {
      if (!this.showsManagedBy) return {};
      return describeManagedBy(item.managing_distributor_id, this.distributors, directLabel(this.$i18n.locale));
    },
    filterSensors(sensors) {
      const lowerSearchText = this.search.toLowerCase();
      return sensors.filter((sensor) => {
        if (this.showsManagedBy && !matchesManagedBy(sensor.managing_distributor_id, this.managedBy)) {
          return false;
        }
        const { name: userName } = sensor.created_by;
        let { organization_name: organizationName } = sensor.created_by;
        organizationName = organizationName || '';

        const keywords = lowerSearchText.split(' ');
        // eslint-disable-next-line arrow-body-style
        return this._.every(keywords, (keyword) => {
          return this._.some([
            this.displayedIdentifier(sensor),
            sensor.name,
            sensor.place,
            sensor.installedOnText,
            sensor.thresholdText,
            userName,
            organizationName,
            this.SENSOR_TYPE_NAMES[sensor.type],
            sensor.managedByName,
          ], (value = '') => {
            const lowerValue = (value || '').toLowerCase();
            return this._.includes(lowerValue, keyword);
          });
        });
      });
    },
    showsIdentifier(item) {
      return item.status !== 'inactive';
    },
    displayedIdentifier(item) {
      return this.showsIdentifier(item) ? (item.device_id || '') : '';
    },
    genItemProps(item, props) {
      if (item.no_response) {
        return {
          ...props,
          class: `${props.class || ''} sensor--warning`,
        };
      }
      return props;
    },
    sortSensors(sensors) {
      // 内部 id は登録日時から生成され識別子と順序が一致しないため、識別子で並べる
      let result = this._.orderBy(sensors, ['type_index', 'device_id', 'id']);
      switch (this.pagination.sortBy) {
        case undefined:
          result = this._.orderBy(result, 'created_by.name');
          result = this._.orderBy(result, 'created_by.organization_name');
          return result;
        default:
          return result;
      }
    },
  },
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'
$warning-color: #edcea9

.admin-sensor-list
  ::v-deep .data-list
    &--list .data-list
      &__item
        margin-left: -24px
        margin-right: -24px
        padding: 24px 24px 24px 72px
        position: relative

        &:not(:last-child)
          border-bottom: 0
          margin-bottom: 1px

          &:after
            background-color: #fff
            bottom: -1px
            content: ''
            height: 1px
            left: 24px
            position: absolute
            right: 24px
            z-index: 1

        .v-icon
          left: 24px
          position: absolute
          top: 24px

      &__field:second-child
        height: 0

    &__item.sensor--warning
      background-color: $warning-color

    &--table .data-list
      &__value--right
        text-align: right

  &__sensor-id,
  &__sensor-name,
  &__installation-site
    overflow-wrap: anywhere

  &__managed-by-select
    // 長い代理店名で幅が伸びて 2 段にならないよう固定し、選択中の名前は省略表示する。
    // スマホ表示では検索欄と同じく横幅いっぱいに広げる
    @media #{map-get($display-breakpoints, 'md-and-up')}
      width: 220px

  &__managed-by
    overflow-wrap: anywhere

  ::v-deep .column
    &--no
      width: 48px

    &--type
      min-width: 180px
      max-width: 180px

    &--id,
    &--name
      min-width: 180px

    &--name
      width: 100%

    &--installed-on
      min-width: 130px

    &--threshold
      min-width: 100px

    &--place,
    &--organization,
    &--user,
    &--managed-by
      width: 180px
      min-width: 140px
</style>
